<script lang="ts" setup generic="TValue extends AcceptableInputValue">
import { CloseIcon } from 'mdi-vue3'
import { type AcceptableInputValue, TagsInputItem, TagsInputItemDelete, TagsInputRoot } from 'reka-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export interface AMultiSelectTagsProperties {
  maxCount?: number
}

export interface AMultiSelectTagsEmits {
  (event: 'delete:index', data: number): void
}

const { t } = useI18n({
  messages: {
    nl: {
      more: 'meer',
    },
    en: {
      more: 'more',
    },
  },
})

const properties = withDefaults(defineProps<AMultiSelectTagsProperties>(), {
  maxCount: 3,
})

const emit = defineEmits<AMultiSelectTagsEmits>()

const modelValue = defineModel<TValue[]>('modelValue', { required: true, default: [] })

const visibleItems = computed((): TValue[] => {
  if (modelValue.value.length > properties.maxCount) {
    return modelValue.value.slice(0, properties.maxCount)
  }
  return modelValue.value
})

function onClickTagDelete(index: number) {
  emit('delete:index', index)
}
</script>

<template>
  <TagsInputRoot v-model="modelValue" class="flex flex-wrap gap-2">
    <TagsInputItem v-for="(item, index) in visibleItems" :key="`tag-${index}-${modelValue.length}`" class="atabix-multiselect-tag" :value="'-'">
      <span class="atabix-multiselect-tag__text">
        <slot :index="index" :item="item" name="item" />
      </span>
      <TagsInputItemDelete @click.stop.prevent="onClickTagDelete(index)">
        <CloseIcon class="size-4" />
      </TagsInputItemDelete>
    </TagsInputItem>
    <TagsInputItem v-if="modelValue.length > maxCount" class="atabix-multiselect-tag atabix-multiselect-tag--overflow" :value="''">
      <span class="atabix-multiselect-tag__text">
        {{ `+${modelValue.length - maxCount} ${t('more')}` }}
      </span>
    </TagsInputItem>
  </TagsInputRoot>
</template>

<style lang="scss" scoped>
.atabix-multiselect-tag {
  @apply rounded-md;
  @apply h-5;
  @apply w-fit;
  @apply flex;
  @apply justify-center;
  @apply items-center;
  @apply grow-0;
  @apply basis-0;
  @apply text-sm;
  @apply font-semibold;
  @apply px-2;
  @apply gap-x-2;
  @apply bg-tag-background;
  @apply text-tag-foreground;
  @apply font-semibold;
}

.atabix-multiselect-tag--overflow {
  @apply text-foreground-subtle #{!important};
  @apply bg-background-hover #{!important};
}

.atabix-multiselect-tag__text {
  @apply whitespace-nowrap;
  @apply break-keep;
}
</style>
